import { BottomSheet, Text, Title, type UseModalState } from "@clipboard-health/ui-components";
import { DialogContent, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import {
  BookabilityDecision,
  type BookabilityStatusItem,
  BookabilityUnmetCriteria,
} from "@src/appV2/OpenShifts/ShiftAction";
import { hasUnmetBookabilityCriteria } from "@src/appV2/OpenShifts/ShiftAction/hasUnmetBookabilityCriteria";
import { useCallback } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { BottomSheetHeader } from "../../components/BottomSheetHeader";
import { DialogFooter } from "../../components/DialogFooter";
import { LoadingButton } from "../../components/LoadingButton";
import {
  SHIFT_DISCOVERY_SHIFT_BOOKED_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_MODAL_PATH,
} from "../../ShiftDiscovery/paths";
import { useShiftModalsDataContext } from "../../ShiftDiscovery/useShiftModalsDataContext";
import { type BlockingRequirementsContext } from "../Bookability/Documents/types";
import { convertModalShiftDataToUseBookShiftParams } from "../Booking/convertModalShiftDataToUseBookShiftParams";
import { useBookShift } from "../Booking/useBookShift";
import { ShiftBottomSheetBlockingRequirementsCard } from "../BottomSheet/BlockingRequirements/Card";
import { ShiftBottomSheetQuizCard } from "../BottomSheet/QuizCard";
import { type ModalShiftData } from "../types";

interface DocumentResolutionBottomSheetProps {
  modalState: UseModalState;
  shift: ModalShiftData;
  blockingRequirementsContext: BlockingRequirementsContext;
  bookabilityStatus: BookabilityStatusItem | undefined;
}

export function DocumentResolutionBottomSheet(props: DocumentResolutionBottomSheetProps) {
  const { modalState, shift, blockingRequirementsContext, bookabilityStatus } = props;

  const history = useHistory();
  const { navigateToModal, computedArrivalData, baseNavigationPath, baseNavigationPathOverride } =
    useShiftModalsDataContext();

  const { visibleBlockingRequirements, hasBlockingRequirements } = blockingRequirementsContext;

  const hasQuizBlocker = hasUnmetBookabilityCriteria(bookabilityStatus, [
    BookabilityUnmetCriteria.WORKER_HAS_NOT_PASSED_QUIZ_FOR_WORKPLACE_RULES,
  ]);

  const workerQuizResultStatus =
    hasQuizBlocker &&
    bookabilityStatus?.attributes.bookability.decision === BookabilityDecision.BLOCKED
      ? bookabilityStatus.attributes.bookability.unmetCriteriaWithMetadata.find(
          (item) =>
            item.unmetCriteria ===
            BookabilityUnmetCriteria.WORKER_HAS_NOT_PASSED_QUIZ_FOR_WORKPLACE_RULES
        )?.metadata?.workerQuizResultStatus
      : undefined;

  const allRequirementsResolved = !hasBlockingRequirements && !hasQuizBlocker;

  function getFooterButtonLabel() {
    if (allRequirementsResolved) {
      return "Book shift";
    }

    if (hasQuizBlocker && hasBlockingRequirements) {
      return "Complete requirements to book";
    }

    if (hasQuizBlocker) {
      return "Complete quiz to book";
    }

    return "Complete documents to book";
  }

  function getModalTitle() {
    if (hasQuizBlocker && hasBlockingRequirements) {
      return "Complete requirements to book";
    }

    if (hasQuizBlocker) {
      return "Complete workplace rules quiz";
    }

    return "Complete required documents";
  }

  function getDescriptionText() {
    if (hasQuizBlocker && hasBlockingRequirements) {
      return "Complete the following requirements before you can book this shift.";
    }

    if (hasQuizBlocker) {
      return "Complete the workplace rules quiz before you can book this shift.";
    }

    if (visibleBlockingRequirements.length === 1) {
      return "This document is required before you can book this shift. Complete it below to continue.";
    }

    return "These documents are required before you can book this shift. Complete them below to continue.";
  }

  const bookShiftParams = convertModalShiftDataToUseBookShiftParams({
    shift,
    navigateToModal,
    onBook: () => {
      navigateToModal(SHIFT_DISCOVERY_SHIFT_BOOKED_MODAL_PATH, {
        pathParams: { shiftId: shift.id },
      });
    },
    computedArrivalData,
    rateNegotiationId: undefined,
  });

  const { attemptBookingShift, isBooking, isLoading } = useBookShift(bookShiftParams);

  useLogEffect(
    APP_V2_USER_EVENTS.SHIFT_DETAILS_DOC_RESOLUTION_VIEWED,
    { shiftId: shift.id },
    { enabled: modalState.modalIsOpen }
  );

  const handleNavigateToDocumentDetails = useCallback(
    (documentDetailsPath: string) => {
      // Replace the current doc-resolution entry in the history stack with shift details,
      // so that browser back from document details returns to shift details (not doc-resolution).
      const effectiveBasePath = baseNavigationPathOverride
        ? `${baseNavigationPath}/${baseNavigationPathOverride}`
        : baseNavigationPath;
      const shiftDetailsPath = generatePath(
        `${effectiveBasePath}/${SHIFT_DISCOVERY_SHIFT_MODAL_PATH}`,
        { shiftId: shift.id }
      );
      history.replace(shiftDetailsPath);
      history.push(documentDetailsPath);
    },
    [history, shift.id, baseNavigationPath, baseNavigationPathOverride]
  );

  const handleBookShift = async () => {
    logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_CTA_BOOK_SHIFT, {
      shiftId: shift.id,
      source: "doc-resolution",
    });
    await attemptBookingShift();
  };

  return (
    <BottomSheet
      modalState={modalState}
      header={
        <BottomSheetHeader
          onBack={() => {
            modalState.closeModal();
          }}
        />
      }
      footer={
        <DialogFooter orientation="vertical" closeButtonAriaLabel="Close document resolution">
          <LoadingButton
            fullWidth
            variant="contained"
            size="large"
            disabled={!allRequirementsResolved}
            isLoading={isLoading || isBooking}
            onClick={handleBookShift}
          >
            {getFooterButtonLabel()}
          </LoadingButton>
        </DialogFooter>
      }
    >
      <DialogContent sx={{ backgroundColor: "unset", width: "100%", padding: 5 }}>
        <Stack direction="column" spacing={5}>
          <Stack spacing={3}>
            <Title variant="h4" component="h4" sx={{ textAlign: "left", textWrap: "balance" }}>
              {getModalTitle()}
            </Title>
            <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
              {getDescriptionText()}
            </Text>
          </Stack>

          {hasQuizBlocker && (
            <Stack spacing={3}>
              <Text semibold variant="h6">
                Workplace Rules Quiz
              </Text>
              <ShiftBottomSheetQuizCard
                shiftId={shift.id}
                quizResultStatus={workerQuizResultStatus}
              />
            </Stack>
          )}

          {visibleBlockingRequirements.length > 0 && (
            <Stack spacing={3}>
              {hasQuizBlocker && (
                <Text semibold variant="h6">
                  Required Documents
                </Text>
              )}
              <Stack spacing={4}>
                {visibleBlockingRequirements.map((requirement) => (
                  <ShiftBottomSheetBlockingRequirementsCard
                    key={requirement.requirementId}
                    blockingRequirement={requirement}
                    shiftId={shift.id}
                    workplaceId={shift.workplace.id}
                    onNavigateToDocDetails={handleNavigateToDocumentDetails}
                  />
                ))}
              </Stack>
            </Stack>
          )}
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
